/* eslint-disable react/no-array-index-key */
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
} from "react";
import { Col, Row, Drawer, Empty, Button, Modal } from "antd";
import { useNavigate } from "react-router-dom";
import {
  DesktopContentWrapper,
  ListingHeaderContentWrapper,
  MobileContentWrapper,
  Wrapper,
} from "./styles";
import { useFlightSearchStore } from "../../store/flightSearch/flightSearchStore";
import {
  SEARCH_DOMESTIC_FLIGHTS_URL,
  SEARCH_INTERNATIONAL_FLIGHTS_URL,
  GET_AIRPORTS_API_URL,
} from "../../utils/constants";
import { getAPI, postAPI } from "../../utils/api";
import { result as mockResult } from "./mock";
import {
  buildFlightSearchPayload,
  buildInternationalTripResults,
  getDurationInMinutes,
} from "./helpers";
import { isMobileScreen } from "../../utils/helpers";
import FlightRequestLoading from "../../components/RequestLoading";
import FlightHeaderNavigation from "../../components/HeaderNavigation/Flight";
import FlightSelectInfo from "../../components/AvailableFlights/FlightSelectInfo";
import ListingFilterMobile from "../../components/ListingFilter/ListingFilterMobile";
import AvailableFlights from "../../components/AvailableFlights";
import ListingFilterDesktop from "../../components/ListingFilter/ListingFilterDesktop";
import Sort from "../../components/ListingFilter/FlightFilters/Sort";
import FlightItinerary from "../../components/FlightItinerary";
import FlightPriceBreakdown from "../../components/FlightItinerary/FlightPriceBreakdown";
import moment, { Moment } from "moment";
import EmptyFlightResult from "../../components/EmptyResult";
import FlightDateFilter from "../../components/AvailableFlights/FlightDateFilter";
import { useStore } from "../../store/global/appStore";
import { useBookingStore } from "../../store/booking/bookingStore";
import { useAuthStore } from "../../store/auth/authStore";
import PriceTagIcon from "../../assets/images/price-tag.svg"
import { MobileDrawerContent, MobileDrawerContentDisclaimer } from "../../components/HeaderNavigation/Flight/styles";
import SearchPriceNotification from "../../components/SearchPriceNotification";
import { FloatingInformation, FloatingInformationImage, FloatingInformationText } from "../../components/SearchPriceNotification/styles";

function SearchListings() {
  const navigate = useNavigate();
  const drawerContainerRef = useRef(null); 
  const mobileDrawerContainerRef = useRef(null);
  const [filteredAvailableFlights, setFilteredAvailableFlights] =
    useState<FlightAggregateResultItem | null>();
  const [availableFlights, setAvailableFlights] =
    useState<FlightAggregateResultItem | null>();
  const [error, setError] = useState<string | null>();
  const [activeAirlineHit, setActiveAirlineHit] =
    useState<FlightResultItem | null>(null);
  const [loadingData, setLoadingData] = useState<boolean>(true);
  const [openDrawer, setOpenDrawer] = useState<boolean>(false);
  const [openModify, setOpenModify] = useState<boolean>(false);
  const [openAllFilters, setOpenAllFilters] = useState<boolean>(false);
  const [selectedAirlines, setSelectedAirlines] = useState<string[]>([]);
  const [selectedStopOver, setSelectedStopOver] = useState<number | null>(null);
  const [selectedFlights, setSelectedFlights] = useState<AirlineResultItem[]>(
    []
  );
  const [openPriceNotification, setPriceNotification] = useState<boolean>(true);
  const [openMinizedPriceNotification, setMinizedPriceNotification] = useState<boolean>(false);

  const loadingMoreRef = useRef(false);

  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  
  const [sortBy, setSortBy] = useState("cheapest");
  const [airlines, setAirlines] = useState<AirlineOption[]>([]);
  const [selectedPriceRange, setSelectedPriceRange] = useState<[number, number] | null>(null);

  const { formData, updateFormData, updateStoreData } = useFlightSearchStore();

  const { removeCredit } = useAuthStore();
  
  useEffect(() => {
    // Clear any applied credits when landing on search listings
    removeCredit();
  }, [removeCredit]);

  const { setReservation } = useBookingStore();

  const isDomestic = formData.isDomestic;
  const isMobile = isMobileScreen();
  const isOneWay = formData.tripType === "one_way";
  const isReturning = selectedFlights.length > 0;
  const { airports, setAirports } = useStore();

  const allAirports = [...airports];

  const lastSearchedPayloadRef = useRef<string>("");

  const searchFlights = useCallback(
    async (payload = formData) => {
      const currentAirports =
        airports.length > 0
          ? airports
          : JSON.parse(localStorage.getItem("airports") || "[]");
      const origin = currentAirports.find(
        (airport: Airport) => airport.Code === payload.OriginLocationCode
      );
      const destination = currentAirports.find(
        (airport: Airport) => airport.Code === payload.DestinationLocationCode
      );
      const resolvedIsDomestic =
        origin && destination
          ? origin.CountryCode === destination.CountryCode
          : (payload.isDomestic ?? false);

      const reformedPayload = buildFlightSearchPayload({
        ...payload,
        isDomestic: resolvedIsDomestic,
      });
      const payloadString = `${resolvedIsDomestic ? "dom" : "intl"}_${JSON.stringify(
        reformedPayload
      )}`;
      if (lastSearchedPayloadRef.current === payloadString) {
        // console.log("Skipping duplicate search flight call");
        return;
      }
      lastSearchedPayloadRef.current = payloadString;

      setFilteredAvailableFlights(null);
      setAvailableFlights(null);
      setLoadingData(true);
      setError(null);
      setSelectedFlights([]);
      const url = resolvedIsDomestic
        ? SEARCH_DOMESTIC_FLIGHTS_URL
        : SEARCH_INTERNATIONAL_FLIGHTS_URL;

      try {
        const searchResults = await postAPI(url, {...reformedPayload, From: 0, pageSize: 50});

        if (searchResults.status === 200) {
          if (resolvedIsDomestic) {
            setAvailableFlights(searchResults.data);
            setFilteredAvailableFlights(searchResults.data);
            setSessionId(searchResults.data?.SessionId || null);
            setHasMore((searchResults.data.ResultList?.[0]?.ResultList?.length || 0) > 0);
            setLoadingData(false);
          } else {
            const reformedData = buildInternationalTripResults(
              searchResults.data
            );
            setAvailableFlights(
              reformedData as unknown as FlightAggregateResultItem
            );
            setFilteredAvailableFlights(
              reformedData as unknown as FlightAggregateResultItem
            );
            setSessionId(searchResults.data?.SessionId || null);
            setHasMore(reformedData.ResultList?.[0]?.ResultList?.length > 0);
          }
          setLoadingData(false);
        } else {
          lastSearchedPayloadRef.current = ""; // Reset to allow retries
          setError(searchResults.data);
          setLoadingData(false);
        }
      } catch (err) {
        lastSearchedPayloadRef.current = ""; // Reset to allow retries
        setError(err);
        setLoadingData(false);
      }
    },
    [formData, airports]
  );

  const loadMoreFlights = useCallback(async () => {
    if (loadingMoreRef.current || !hasMore) return;
    loadingMoreRef.current = true;
    setLoadingMore(true);

    const nextPage = page + 1;
    const url = isDomestic
      ? SEARCH_DOMESTIC_FLIGHTS_URL
      : SEARCH_INTERNATIONAL_FLIGHTS_URL;
    const reformedPayload = buildFlightSearchPayload(formData);
    const searchResults = await postAPI(url, { ...reformedPayload, From: nextPage, pageSize: 50, SessionId: sessionId });

    if (searchResults.status === 200) {
      const newData = isDomestic
        ? searchResults.data
        : buildInternationalTripResults(searchResults.data);

      const newDepartures = newData?.ResultList?.[0]?.ResultList || [];

      if (newDepartures.length === 0) {
        setHasMore(false);
      } else {
        setAvailableFlights((prev: any) => ({
          ...prev,
          ResultList: prev?.ResultList?.map((leg: any, i: number) => ({
            TotalCount: leg.TotalCount,
            ResultList: [...leg.ResultList, ...(newData.ResultList[i]?.ResultList || [])],
          })),
        }));
        setFilteredAvailableFlights((prev: any) => ({
          ...prev,
          ResultList: prev?.ResultList?.map((leg: any, i: number) => ({
            TotalCount: leg.TotalCount,
            ResultList: [...leg.ResultList, ...(newData.ResultList[i]?.ResultList || [])],
          })),
        }));
        setPage(nextPage);
      }
    }

    loadingMoreRef.current = false;
    setLoadingMore(false);
  }, [page, hasMore, formData, isDomestic, sessionId]);

  useEffect(() => {
    setReservation(null, "confirmData");
  }, []);

  const cheapestFares = useMemo(() => {
    if (!availableFlights || availableFlights.TotalCount === 0) {
      return null;
    }

    const cheapestDeparture =
      availableFlights.ResultList[0].ResultList[0].TotalFare;
    const cheapestReturn = availableFlights.ResultList[1]
      ? availableFlights.ResultList[1].ResultList[0].TotalFare
      : null;

    return {
      cheapestDeparture,
      cheapestReturn,
    };
  }, [availableFlights]);

  const farePriceRange = useMemo(() => {
    if (!availableFlights || availableFlights.TotalCount === 0) {
      return { min: 0, max: 0 };
    }

    if (selectedFlights.length === 0) {
      const departureFares = availableFlights.ResultList[0].ResultList.map(
        (f) => f.TotalFare
      );
      return {
        min: Math.min(...departureFares),
        max: Math.max(...departureFares),
      };
    }

    if (selectedFlights.length === 1) {
      const returnFares = availableFlights.ResultList[1]
        ? availableFlights.ResultList[1].ResultList.map((f) => f.TotalFare)
        : null;
      return returnFares
        ? { min: Math.min(...returnFares), max: Math.max(...returnFares) }
        : { min: 0, max: 0 };
    }

    return { min: 0, max: 0 };
  }, [availableFlights, selectedFlights.length]);

  const DrawerFooter = (
      <Button type="primary" block onClick={() => setMinizedPriceNotification(true)}>
        Got it
      </Button>
  )

  const handleMinizeNotification = () => {
    setPriceNotification(false);
    setMinizedPriceNotification(true)
  }

  const handleResetFilters = () => {
    setSelectedAirlines(airlines.map((a) => a.code));
    setSelectedStopOver(null);
    setSortBy("cheapest");
    setSelectedPriceRange(null);
    setFilteredAvailableFlights(availableFlights);
  };

  const renderAvailableFlights = () => (
    <AvailableFlights
      results={filteredAvailableFlights as unknown as FlightAggregateResultItem}
      loading={loadingData}
      onBookingRedirect={handleBookingRedirect}
      selectedFlights={selectedFlights}
      onFlightSelect={(ar) => {
        handleResetFilters();
        setSelectedFlights(ar);
      }}
      cheapestFares={cheapestFares as unknown as { cheapestDeparture: number; cheapestReturn: number | null }}
      onLoadMore={loadMoreFlights}
      loadingMore={loadingMore} 
    />
  );

  const handleNavigation = () => {
    if (activeAirlineHit) {
      setActiveAirlineHit(null);
    } else {
      navigate(-1);
    }
  };

  const handleBookingRedirect = (selectedFlight: AirlineResultItem[]) => {
    updateStoreData({ selectedFlight });
    if (isMobile) {
      navigate("/itinerary/flight");
    } else {
      setOpenDrawer(true);
    }
  };

  const handleAirlineFiltering = (selectedAirlines: string[]) => {
    if (
      selectedAirlines.length === 0 ||
      selectedAirlines.length === airlines.length
    ) {
      setFilteredAvailableFlights(availableFlights);
      handleSort(sortBy, availableFlights);
      setSelectedAirlines(selectedAirlines);
    } else {
      const isReturning = selectedFlights.length > 0 && isDomestic;
      const resultIndex = isReturning ? 1 : 0;
      const unFilteredResultIndex = isReturning ? 0 : 1;
      const resultItems = availableFlights?.ResultList[resultIndex];

      const filtered = resultItems?.ResultList.filter((resultItem) =>
        selectedAirlines.some((airline) =>
          resultItem.FlightDetails.map(
            (fd) => fd.OperatingAirlineCode
          ).includes(airline)
        )
      );

      const totalFiltered = filtered?.length;

      let filteredResultList = [];

      if (availableFlights?.ResultList[unFilteredResultIndex]) {
        filteredResultList[unFilteredResultIndex] = {
          TotalCount:
            availableFlights.ResultList[unFilteredResultIndex].TotalCount,
          ResultList:
            availableFlights.ResultList[unFilteredResultIndex].ResultList,
        };
      }

      filteredResultList[resultIndex] = {
        TotalCount: totalFiltered,
        ResultList: filtered,
      };
      const newFiltered = {
        TotalCount: availableFlights?.TotalCount,
        ResultList: filteredResultList,
      };

      setFilteredAvailableFlights(newFiltered as  FlightAggregateResultItem);
      handleSort(sortBy, newFiltered as  FlightAggregateResultItem);
      setSelectedAirlines(selectedAirlines);
    }
  };

  const handleStopoverFiltering = (stopOver: number | null) => {
    if (stopOver === -1 || stopOver === null) {
      setFilteredAvailableFlights(availableFlights);
      handleSort(sortBy, availableFlights);
      setSelectedStopOver(stopOver);
    } else {
      // Stopover filtering is only applicable for international flights
      const resultItems = availableFlights?.ResultList[0];
      const filteredFlightResultList: AirlineResultItem[] = [];
      resultItems?.ResultList.forEach((f) => {
        const findStopOver = f.FlightDetails.find(
          (fd) => fd.StopOvers === stopOver
        );
        if (findStopOver) {
          filteredFlightResultList.push(f);
        }
      });

      const totalFiltered = filteredFlightResultList.length;

      const filteredResults = {
        TotalCount: availableFlights?.TotalCount,
        ResultList: [
          {
            TotalCount: totalFiltered,
            ResultList: filteredFlightResultList,
          },
        ],
      };

      setFilteredAvailableFlights(filteredResults as  FlightAggregateResultItem);
      handleSort(sortBy, filteredResults as  FlightAggregateResultItem);
      setSelectedStopOver(stopOver);
    }
  };

  const handleApplyAllFlightFilters = (filters: {
    airlines: string[];
    price: [number, number];
    stops: number | null;
    sortBy: string | null;
  }) => {
    if (!availableFlights) return;

    const isReturning = selectedFlights.length > 0 && isDomestic;
    const resultIndex = isReturning ? 1 : 0;
    const unFilteredResultIndex = isReturning ? 0 : 1;
    let resultItems: AirlineResultItem[] = availableFlights.ResultList[resultIndex]?.ResultList || [];

    // 1. Airline filter
    if (filters.airlines.length > 0 && filters.airlines.length < airlines.length) {
      resultItems = resultItems.filter((resultItem) =>
        filters.airlines.some((airline) =>
          resultItem.FlightDetails.map((fd) => fd.OperatingAirlineCode).includes(airline)
        )
      );
    }
    setSelectedAirlines(filters.airlines);

    // 2. Price filter
    const [min, max] = filters.price;
    resultItems = resultItems.filter((a: AirlineResultItem) => a.TotalFare >= min && a.TotalFare <= max);
    setSelectedPriceRange(filters.price);

    // 3. Stopover filter (only for international / index 0)
    if (!isReturning && filters.stops !== null && filters.stops !== -1) {
      resultItems = resultItems.filter((f) =>
        f.FlightDetails.some((fd) => fd.StopOvers === filters.stops)
      );
    }
    setSelectedStopOver(filters.stops);

    // 4. Build merged result list
    const filteredResultList: any[] = [];
    if (availableFlights.ResultList[unFilteredResultIndex]) {
      filteredResultList[unFilteredResultIndex] = availableFlights.ResultList[unFilteredResultIndex];
    }
    filteredResultList[resultIndex] = {
      TotalCount: resultItems.length,
      ResultList: resultItems,
    };

    const newFiltered = {
      ...availableFlights,
      TotalCount: resultItems.length,
      ResultList: filteredResultList,
    } as FlightAggregateResultItem;

    setFilteredAvailableFlights(newFiltered);
    handleSort(filters.sortBy, newFiltered);
  };

  const handleSort = (value: string | null, data = filteredAvailableFlights) => {
    setSortBy(value as string);
    switch (value) {
      case "cheapest":
        handleSortByPrice("asc", data);
        return;
      case "fastest":
        handleSortByDuration("asc", data as FlightAggregateResultItem);
        return;
      case "earliest":
        handleSortByDate("desc", data as FlightAggregateResultItem);
        return;
    }
  };

  const handleSortByPrice = (
    order: string | null,
    data = filteredAvailableFlights
  ) => {
    const isReturning = selectedFlights.length > 0;
    const resultIndex = isReturning ? 1 : 0;
    const filteredResults = data || filteredAvailableFlights;
    if (!filteredResults) {
      return;
    }

    const sortedRecords =
      order === "asc"
        ? filteredResults.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              a.TotalFare - b.TotalFare
          )
        : filteredResults.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              b.TotalFare - a.TotalFare
          ) || [];

    const sortedResultList = filteredResults.ResultList;
    sortedResultList[resultIndex] = {
      TotalCount: filteredResults.ResultList[resultIndex].TotalCount,
      ResultList: sortedRecords,
    };

    setFilteredAvailableFlights({
      ...filteredResults,
      ResultList: sortedResultList,
    });
  };

  const handleFilterByPriceRange = (range: [number, number]) => {
    setSelectedPriceRange(range);
    const [min, max] = range;
    const isReturning = selectedFlights.length > 0;
    const resultIndex = isReturning ? 1 : 0;
    if (!availableFlights) {
      return;
    }

    const flights = { ...availableFlights };
    const filteredResults = flights.ResultList[resultIndex].ResultList.filter(
      (a: AirlineResultItem) => a.TotalFare >= min && a.TotalFare <= max
    );

    const sortedResultList = [...flights.ResultList];
    sortedResultList[resultIndex] = {
      TotalCount: filteredResults.length,
      ResultList: filteredResults,
    };

    const newFiltered = {
      ...flights,
      TotalCount: filteredResults.length,
      ResultList: sortedResultList,
    };

    setFilteredAvailableFlights(newFiltered);
    handleSort(sortBy, newFiltered);
  };

  const handleSortByDuration = (order: string | null, data: FlightAggregateResultItem | null) => {
    const isReturning = selectedFlights.length > 0;
    const resultIndex = isReturning ? 1 : 0;
    const filteredResults = data || filteredAvailableFlights;
    if (!filteredResults) {
      return;
    }

    const sortedRecords =
      order === "asc"
        ? filteredResults?.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              getDurationInMinutes(a.TotalFlightDuration!) -
              getDurationInMinutes(b.TotalFlightDuration!)
          )
        : filteredResults.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              getDurationInMinutes(b.TotalFlightDuration!) -
              getDurationInMinutes(a.TotalFlightDuration!)
          ) || [];
    //
    const sortedResultList = filteredResults.ResultList;
    sortedResultList[resultIndex] = {
      TotalCount: filteredResults.ResultList[resultIndex].TotalCount,
      ResultList: sortedRecords,
    };

    setFilteredAvailableFlights({
      ...filteredResults,
      ResultList: sortedResultList,
    });
  };

  useEffect(() => {
    if (!availableFlights) return;

    let uniqueAirlines: { code: string; name: string }[] = [];
    if (availableFlights) {
      const legIndex = isReturning && isDomestic ? 1 : 0;
      const legResults = availableFlights.ResultList[
        legIndex
      ] as FlightResultItem;
      uniqueAirlines = legResults.ResultList.map((d) => {
        return {
          code: d.FlightDetails[0].OperatingAirlineCode,
          name: d.FlightDetails[0].OperatingAirlineName,
        };
      });
    }

    const filteredUniqueAirlines = uniqueAirlines.filter(
      (a, i) => uniqueAirlines.findIndex((s) => a.code === s.code) === i
    );
    setAirlines([...filteredUniqueAirlines]);
    setSelectedAirlines(filteredUniqueAirlines.map((a) => a.code));
    setSelectedStopOver(null);
    setSelectedPriceRange(null);
  }, [availableFlights, isReturning, isDomestic]);

  useEffect(() => {
    if (airports.length === 0) {
      const fetchAirports = async () => {
        try {
          const airportsApi = await getAPI(GET_AIRPORTS_API_URL);
          if (airportsApi.status === 200) {
            localStorage.setItem("airports", JSON.stringify(airportsApi.data));
            setAirports(airportsApi.data, "airports");
          }
        } catch (e) {
          console.error("Error fetching airports in listings mount:", e);
        }
      };
      fetchAirports();
    }
  }, [airports, setAirports]);

  useEffect(() => {
    const origin = allAirports.find(
      (airport) => airport.Code === formData.OriginLocationCode
    );
    const destination = allAirports.find(
      (airport) => airport.Code === formData.DestinationLocationCode
    );

    if (!origin || !destination) return;
    const isDomestic = origin?.CountryCode === destination?.CountryCode;

    if (formData.isDomestic !== isDomestic) {
      updateFormData({ isDomestic });
    }
  }, [airports, formData.OriginLocationCode, formData.DestinationLocationCode]);

  const handleSortByDate = (order: string | null, data: FlightAggregateResultItem | null) => {
    const isReturning = selectedFlights.length > 0;
    const resultIndex = isReturning ? 1 : 0;
    const filteredResults = data || filteredAvailableFlights;
    if (!filteredResults) {
      return;
    }

    const sortedRecords =
      order === "asc"
        ? filteredResults.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              moment(b.FlightDetails[0].DepartureDate).diff(
                moment(a.FlightDetails[0].DepartureDate)
              )
          )
        : filteredResults.ResultList[resultIndex].ResultList.sort(
            (a: AirlineResultItem, b: AirlineResultItem) =>
              moment(a.FlightDetails[0].DepartureDate).diff(
                moment(b.FlightDetails[0].DepartureDate)
              )
          ) || [];

    const sortedResultList = filteredResults.ResultList;
    sortedResultList[resultIndex] = {
      TotalCount: filteredResults.ResultList[resultIndex].TotalCount,
      ResultList: sortedRecords,
    };

    setFilteredAvailableFlights({
      ...filteredResults,
      ResultList: sortedResultList,
    });
  };

  const getDrawerContainer = () =>
    drawerContainerRef.current as unknown as HTMLElement;

  const handleDateChange = (date: Moment) => {
    updateFormData({
      DepartureDate: date,
    });
  };

  useEffect(() => {
    if (airports && airports.length > 0) {
      searchFlights();
    }
  }, [searchFlights, airports]);

  const hasResults = availableFlights?.TotalCount! > 0;
  const resultCount = useMemo(() => {
    if (isDomestic && !isOneWay) {
      return isReturning
        ? filteredAvailableFlights?.ResultList[1]?.TotalCount || 0
        : filteredAvailableFlights?.ResultList[0]?.TotalCount || 0;
    }

    return filteredAvailableFlights?.ResultList[0]?.TotalCount || 0;
  }, [filteredAvailableFlights, isReturning]);

  const handleEmptyResult = () => {
    if (hasResults) {
      handleResetFilters();
    } else {
      setOpenModify(true);
    }
  };

  if (loadingData) {
    return <FlightRequestLoading />;
  }

  return (
    <Wrapper>
      <Row>
        <Col span={24}>
          <FlightHeaderNavigation
            refetchSearch={searchFlights}
            navigateHandler={handleNavigation}
            modify={openModify}
            onModifyChange={(value) => setOpenModify(value)}
            onOpenFilters={() => setOpenAllFilters(true)}
          />
        </Col>
      </Row>

      {/*  <= XS Devices */}
      <Row>
        <Col xs={24} lg={0}>
          {isOneWay && (
            <FlightDateFilter
              handleDateChange={handleDateChange}
              refetchSearch={searchFlights}
            />
          )}
          <MobileContentWrapper ref={mobileDrawerContainerRef}>
            <Row>
              <Col xs={24} lg={0}>
                <FlightSelectInfo
                  count={resultCount}
                  isDomestic={isDomestic}
                  isReturning={isReturning}
                />
                <ListingFilterMobile
                  isDomestic={isDomestic}
                  handleStopoverFiltering={handleStopoverFiltering}
                  handleAirlineFiltering={handleAirlineFiltering}
                  handleSort={handleSort}
                  sortBy={sortBy}
                  handleFilterByPriceRange={handleFilterByPriceRange}
                  farePriceRange={farePriceRange}
                  selectedAirlines={selectedAirlines}
                  airlines={airlines}
                  selectedStopOver={selectedStopOver}
                  openAllFilters={openAllFilters}
                  setOpenAllFilters={setOpenAllFilters}
                  onApplyAllFilters={handleApplyAllFlightFilters}
                />

                {(error || filteredAvailableFlights?.TotalCount === 0) ? (
                  <EmptyFlightResult
                    onAction={handleEmptyResult}
                    emptyOnFilter={!error && hasResults}
                  />
                ) : (
                  renderAvailableFlights()
                )}
              </Col>
            </Row>

            {/* <Drawer
              open={openPriceNotification && filteredAvailableFlights?.ResultList && filteredAvailableFlights?.ResultList?.length > 0 }
              onClose={handleMinizeNotification}
              headerStyle={{ display: "none" }} 
              placement="bottom"
              height="35%"
              getContainer={mobileDrawerContainerRef.current as unknown as HTMLElement}
              closable={true}
            >
              <MobileDrawerContentDisclaimer>
                <SearchPriceNotification onClick={handleMinizeNotification}  />
              </MobileDrawerContentDisclaimer>
            </Drawer> */}
          </MobileContentWrapper>
        </Col>
      </Row>

      {/*  Desktop devices*/}
      <Row>
        <Col xs={0} lg={24}>
          <DesktopContentWrapper className="container" ref={drawerContainerRef}>
            {/* i want to add a gap of resposive gap, 10px on small devices, 20px on large devices and 50px on medium devices, between the filter section and the main content */}
            <Row gutter={[{ xs: 10, sm: 10, md: 50, lg: 20, xl: 50 }, 15]}>
              <Col lg={6} xl={6}>
                <ListingFilterDesktop
                  isDomestic={isDomestic}
                  handleStopoverFiltering={handleStopoverFiltering}
                  handleAirlineFiltering={handleAirlineFiltering}
                  handleFilterByPriceRange={handleFilterByPriceRange}
                  onResetFilters={handleResetFilters}
                  farePriceRange={farePriceRange}
                  selectedAirlines={selectedAirlines}
                  selectedStopOver={selectedStopOver}
                  airlines={airlines}
                  selectedPriceRange={selectedPriceRange}
                />
              </Col>
              <Col lg={18} xl={18}>
                {isOneWay && (
                  <FlightDateFilter
                    handleDateChange={handleDateChange}
                    refetchSearch={searchFlights}
                  />
                )}
                <ListingHeaderContentWrapper>
                  <FlightSelectInfo
                    isDomestic={isDomestic}
                    count={resultCount}
                    isReturning={isReturning}
                  />
                  <Sort onChange={handleSort} sortBy={sortBy} />
                </ListingHeaderContentWrapper>

                {(error || filteredAvailableFlights?.TotalCount === 0) ? (
                  <EmptyFlightResult
                    onAction={handleEmptyResult}
                    emptyOnFilter={!error && hasResults}
                  />
                ) : (
                  renderAvailableFlights()
                )}
              </Col>
            </Row>

            <Drawer
              title="Flight Details"
              placement="right"
              onClose={() => setOpenDrawer(false)}
              open={openDrawer}
              footer={<FlightPriceBreakdown />}
              getContainer={getDrawerContainer}
            >
              <FlightItinerary />
            </Drawer>
            {/* <Modal
                className="custom-rounded-modal-32"
                open={openPriceNotification && filteredAvailableFlights?.ResultList && filteredAvailableFlights?.ResultList?.length > 0 }
                footer={null}
                onCancel={handleMinizeNotification}
                width={580}
                getContainer={false}
                forceRender
            >
              <SearchPriceNotification onClick={handleMinizeNotification}  />
            </Modal> */}
          </DesktopContentWrapper>
        </Col>
        { openMinizedPriceNotification && filteredAvailableFlights?.ResultList && filteredAvailableFlights?.ResultList?.length > 0  ? <FloatingInformation>
        <FloatingInformationImage src={PriceTagIcon} alt="price tag" />
        <FloatingInformationText>Prices include all fees</FloatingInformationText>
      </FloatingInformation> : null}
      </Row>
    </Wrapper>
  );
}

export default SearchListings;
